/**
 * Cache module-level de zonas de delivery por tenantId.
 *
 * Patrón "prefetch eager + memoria del cliente primero" (feedback memory):
 *   - WaCoverageCheckCard hace prefetch al mount (background, no bloquea)
 *   - WaCoverageMapModal lee de este cache → render instantáneo (~0ms)
 *
 * Cache TTL: 5min. Las zonas cambian raramente (admin las edita en Settings),
 * y un staleness corto no afecta UX en el chat.
 */

import { supabase } from '@/lib/supabase';

export interface DeliveryZone {
  id: string;
  district_name: string | null;
  zone_name: string | null;
  delivery_fee: number;
  zone_type: string | null;
  polygon_coordinates: [number, number][] | null;
}

interface CacheEntry {
  tenantId: string;
  zones: DeliveryZone[];
  fetchedAt: number;
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes

// Map<conversationId, CacheEntry> — la entrada se indexa por conv pero
// solo por conveniencia del caller. tenantId está en el value.
const TENANT_BY_CONV = new Map<string, string>();
const ZONES_BY_TENANT = new Map<string, CacheEntry>();
// In-flight promises para deduplicar fetches concurrentes (race-safe).
const INFLIGHT = new Map<string, Promise<DeliveryZone[]>>();

/**
 * Devuelve zonas para una conversación, usando cache cuando sea posible.
 * Si no hay cache válido, hace fetch + lo guarda. Idempotente y race-safe.
 */
export async function getDeliveryZonesForConv(conversationId: string): Promise<DeliveryZone[]> {
  if (!conversationId) return [];

  // Si ya resolvimos tenant para esta conv, verificar cache de zonas
  let tenantId = TENANT_BY_CONV.get(conversationId);
  if (tenantId) {
    const cached = ZONES_BY_TENANT.get(tenantId);
    if (cached && Date.now() - cached.fetchedAt < TTL_MS) {
      return cached.zones;
    }
  }

  // Deduplicar: si ya hay un fetch en vuelo para esta conv, esperarlo
  const inflight = INFLIGHT.get(conversationId);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      // 1) Resolver tenant si no lo tenemos
      if (!tenantId) {
        const { data: conv } = await supabase
          .from('wa_conversations')
          .select('tenant_id')
          .eq('id', conversationId)
          .maybeSingle();
        if (!conv) return [];
        tenantId = (conv as { tenant_id: string }).tenant_id;
        TENANT_BY_CONV.set(conversationId, tenantId);
      }

      // 2) Re-check cache (puede haberse poblado por otro caller mientras tanto)
      const cached = ZONES_BY_TENANT.get(tenantId);
      if (cached && Date.now() - cached.fetchedAt < TTL_MS) {
        return cached.zones;
      }

      // 3) Fetch zones
      const { data, error } = await supabase
        .from('ecommerce_delivery_districts')
        .select('id, district_name, zone_name, delivery_fee, zone_type, polygon_coordinates')
        .eq('tenant_id', tenantId);
      if (error) return [];
      const zones = (data ?? []) as DeliveryZone[];
      ZONES_BY_TENANT.set(tenantId, { tenantId, zones, fetchedAt: Date.now() });
      return zones;
    } finally {
      INFLIGHT.delete(conversationId);
    }
  })();

  INFLIGHT.set(conversationId, promise);
  return promise;
}

/**
 * Pre-fetch eager: dispara la carga sin esperar resultado. Llamar desde
 * `WaCoverageCheckCard` al mount para que cuando el operador abra el
 * modal, las zonas ya estén en cache.
 */
export function prefetchDeliveryZones(conversationId: string): void {
  void getDeliveryZonesForConv(conversationId);
}

/**
 * Versión síncrona: devuelve zonas si están en cache, sino null.
 * Usar en el modal para render inicial sin loading state.
 */
export function getCachedZonesForConv(conversationId: string): DeliveryZone[] | null {
  const tenantId = TENANT_BY_CONV.get(conversationId);
  if (!tenantId) return null;
  const cached = ZONES_BY_TENANT.get(tenantId);
  if (!cached) return null;
  if (Date.now() - cached.fetchedAt >= TTL_MS) return null;
  return cached.zones;
}
